import type { I18nText } from "./api";

/**
 * Resolution order:
 *   1. Exact match (e.g. 'pt-BR' → text['pt-BR'])
 *   2. Bare-language match (e.g. 'pt-BR' → text['pt']) — most stored
 *      titles use the bare form, so a viewer with locale='pt-BR' would
 *      otherwise fall through to English.
 *   3. text.en
 *   4. Any non-empty value
 *   5. Empty string
 */
export function pickLocale(text: I18nText, locale = "en"): string {
  const base = locale.split("-")[0];
  return (
    text[locale] ??
    (base !== locale ? text[base] : undefined) ??
    text.en ??
    Object.values(text).find(Boolean) ??
    ""
  );
}

/**
 * Format a per-share price as a `¢` string. `cents` is integer cents
 * per WHOLE share (1–99). Caller may pass a fractional number for
 * mid-price displays; rounded down to nearest integer.
 *
 * Canonical entry-point for the orderbook / market / trade-panel
 * "21¢" surface. The companion `formatPriceDollars` returns "$0.21"
 * for the rare contexts that need the dollar shape (e.g. raw notional
 * display on order receipts). Picking one of these two is a deliberate
 * call — never inline `(cents / 100).toFixed(2)¢` (that produces
 * "0.21¢" which is gibberish — literally "0.21 cents").
 */
export function formatPriceCents(cents: number): string {
  return `${Math.floor(cents)}¢`;
}

/**
 * Per-share price in dollar shape (`$0.21`). Use only when the
 * dollar shape is contextually required (e.g. order receipts that
 * mix dollar and share columns); default to `formatPriceCents` for
 * orderbook / trade-panel / position-card surfaces — those are
 * standardized on the `21¢` shape.
 */
export function formatPriceDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Format a centi-share count (post migration 0145, every share-typed
 * BIGINT in the API stores `whole_shares × 100`) as a fractional
 * shares string. Trims trailing `.00` for integer counts so
 * whole-share quantities read clean (`"3"` not `"3.00"`) while
 * fractional counts get 2-decimal precision (`"3.84"`).
 *
 * Accepts the raw forms the API may surface — `string` (BIGINT
 * column serialized to JSON), `number`, or `bigint`. Returns "—"
 * for non-finite / nullish input so renderers don't have to guard.
 */
export function formatShares(
  centiShares: string | number | bigint | null | undefined,
): string {
  if (centiShares === null || centiShares === undefined) return "—";
  const n = typeof centiShares === "bigint" ? Number(centiShares) : Number(centiShares);
  if (!Number.isFinite(n)) return "—";
  const whole = n / 100;
  if (Number.isInteger(whole)) return String(whole);
  return whole.toFixed(2);
}

/**
 * Position value in USDC cents from centi-shares × per-whole-share price.
 *
 * Math:   USDC cents = floor(centi_shares × price_cents_per_share / 100)
 *
 * Why this exists: shares post-migration-0145 are centi-shares
 * (1 share = 100 centi-shares). Per-share price is cents-per-WHOLE-share
 * (1–99). Multiplying directly yields share-cents, NOT USDC cents —
 * divide by 100 to translate. The 2026-05-11 founder report ("YES
 * position worth $18,581 — where is the money coming from?") was a
 * `formatMoney(Math.round(centi × price * 100 / 100), …)` site that
 * skipped the divide-by-100 and produced a 100× overstatement.
 *
 * All position-value renders go through this helper. No component does
 * raw `(shares * price) / 100` inline — that's how three different
 * surfaces drifted in three different directions before this helper
 * existed. Same shape as the matcher's `centiShareNotionalCents`.
 *
 * Accepts the raw forms the API surfaces — `string` (BIGINT) for
 * `centiShares` and `string | number` for `priceCents` (since
 * positions.avg_cost_cents is NUMERIC and can carry fractional cents
 * for users who DCA'd across multiple fills).
 */
export function positionValueCents(
  centiShares: string | number | bigint,
  pricePerShareCents: string | number,
): number {
  const centi =
    typeof centiShares === "bigint" ? Number(centiShares) : Number(centiShares);
  const price = Number(pricePerShareCents);
  if (!Number.isFinite(centi) || !Number.isFinite(price)) return 0;
  return Math.floor((centi * price) / 100);
}

/**
 * Locale-aware volume display.
 *
 * `cents` is matcher-side USDC cents (the canonical unit). When the
 * viewer's display currency is BRL (Brazilian region) and an fx
 * rate is available, the volume is projected to BRL — symbol flips
 * R$ and the K/M thresholds scale accordingly. Falls back to the
 * USDC-anchored "$X" rendering when no rate is available (no leak
 * of foreign-currency projection per the 2026-05-03 jurisdiction-
 * scoping rule).
 *
 * `usdcPerQuote` is the rate from useFxRate (e.g. quote=BRL returns
 * "1 USDC = N BRL"). To convert USDC cents → quote cents:
 * `usdcCents × rate`.
 *
 * Callers from "use client" components pass the hook outputs:
 *   const display = useDisplayCurrency();      // BRL | USDC | USD
 *   const fx = useFxRate("BRL");               // null off-region
 *   formatVolumeCents(o.volumeCents, display, fx?.rate);
 *
 * Pre-2026-05-19 the function hardcoded "$" which leaked the USD
 * symbol onto every BR locale page (matcher-unit cents rendered as
 * USDC but labelled $, with no BRL projection at all).
 */
export function formatVolumeCents(
  cents: string,
  displayCurrency: MoneyCurrency = "USD",
  usdcPerQuote: number | null = null,
): string {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "—";
  let displayCents = n;
  let symbol = "$";
  if (
    displayCurrency === "BRL" &&
    typeof usdcPerQuote === "number" &&
    usdcPerQuote > 0
  ) {
    displayCents = n * usdcPerQuote;
    symbol = "R$";
  }
  const units = displayCents / 100;
  if (units >= 1_000_000) return `${symbol}${(units / 1_000_000).toFixed(1)}M`;
  if (units >= 1_000) return `${symbol}${(units / 1_000).toFixed(1)}K`;
  return `${symbol}${Math.round(units)}`;
}

/**
 * EPIC-028 STORY-1602 — locale-aware money formatter.
 *
 * Closed-beta supports BRL + USD. Caller passes the currency
 * (resolved from RegionContext or from the wallet's `currency`
 * column); the formatter handles the symbol + thousands/decimal
 * separator per locale.
 *
 *   formatMoney(12345, "BRL") → "R$123,45"  (pt-BR)
 *   formatMoney(12345, "USD") → "$123.45"   (en-US)
 *   formatMoney(12345678, "BRL") → "R$123.456,78"
 *
 * `cents` accepts string (from BigInt-typed API responses) or
 * number. Floors fractional input to integer cents.
 */
export type MoneyCurrency =
  | "BRL"
  | "USD"
  | "USDC"
  | "MXN"
  | "CAD"
  | "INR";

/**
 * Region-immersion extension (2026-05-21). Each Phase-1 fiat carries
 * its own locale + symbol + decimal/grouping convention; formatMoney
 * picks the right branch from this map so the en-CA "$" doesn't get
 * swapped for "R$" when a Canadian viewer lands on /home. INR uses
 * the en-IN locale to emit "₹" with the Indian lakh/crore grouping
 * (1,23,45,678.90), not en-US grouping.
 *
 * USD + USDC kept inline because USDC carries the trailing " USDC"
 * marker (per CLAUDE.md "USDC display matches USD for closed beta").
 */
const FIAT_FORMAT_TABLE: Partial<
  Record<MoneyCurrency, { locale: string; symbol: string }>
> = {
  BRL: { locale: "pt-BR", symbol: "R$" },
  MXN: { locale: "es-MX", symbol: "$" },
  CAD: { locale: "en-CA", symbol: "$" },
  INR: { locale: "en-IN", symbol: "₹" },
};

/**
 * EPIC-029 STORY-1805 — fx-converted display.
 *
 * Convert cents-amount-in-source-currency to cents-amount-in-target.
 * Closed-beta path: source is always BRL (the matcher currency).
 * Target is whatever the user chose via the locale picker / display
 * pref.
 *
 * `usdcPerSourceUnit` is the fx rate "1 USDC = N source", which is
 * what FxRatesService.getRate('BRL') returns (e.g. 5.0 means 1 USDC
 * = 5 BRL). To convert source → USDC: divide by rate. To convert
 * source → USD: same as USDC for closed beta (USDC ≈ USD at 1:1).
 *
 * If source === target, no conversion (rate-independent identity).
 * If rate is null/invalid, returns null so callers can fallback.
 */
export function convertCents(
  cents: number,
  source: MoneyCurrency,
  target: MoneyCurrency,
  usdcPerSourceUnit: number | null,
): number | null {
  if (source === target) return cents;
  // USD ↔ USDC are 1:1 by closed-beta convention.
  if (
    (source === "USD" && target === "USDC") ||
    (source === "USDC" && target === "USD")
  ) {
    return cents;
  }
  const FIATS: MoneyCurrency[] = ["BRL", "MXN", "CAD", "INR"];
  const isFiat = (c: MoneyCurrency) => FIATS.includes(c);
  const isDollarLike = (c: MoneyCurrency) => c === "USD" || c === "USDC";
  // FIAT → USD/USDC: divide by rate (e.g. BRL cents / 5.0 = USDC cents).
  if (isFiat(source) && isDollarLike(target)) {
    if (usdcPerSourceUnit === null || usdcPerSourceUnit <= 0) return null;
    return Math.round(cents / usdcPerSourceUnit);
  }
  // USD/USDC → FIAT: multiply by rate (e.g. USDC cents * 5.0 = BRL cents).
  // Region-immersion extension (2026-05-21) generalises this branch
  // from BRL-only to the full Phase-1 fiat set — STORY-1700 part 2
  // mints in USDC, so MX/CA/IN viewers hit this path on first render
  // the same way BR viewers do.
  if (isDollarLike(source) && isFiat(target)) {
    if (usdcPerSourceUnit === null || usdcPerSourceUnit <= 0) return null;
    return Math.round(cents * usdcPerSourceUnit);
  }
  // Any cross-fiat pair (e.g. BRL → MXN) needs a second leg via its
  // own quote — filed for when cross-fiat markets land.
  return null;
}

export function formatMoney(
  cents: string | number,
  currency: MoneyCurrency = "BRL",
): string {
  const n = typeof cents === "string" ? Number(cents) : cents;
  if (!Number.isFinite(n)) return "—";
  const value = Math.trunc(n) / 100;
  const fiat = FIAT_FORMAT_TABLE[currency];
  if (fiat) {
    // Region-immersion extension (2026-05-21) — BRL/MXN/CAD/INR each
    // route through their own locale + symbol via the FIAT_FORMAT_TABLE
    // map so the Canadian viewer never sees "R$" and the Indian viewer
    // gets en-IN lakh/crore grouping.
    return `${fiat.symbol}${value.toLocaleString(fiat.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  if (currency === "USDC") {
    // USDC display matches USD for closed beta.
    return `$${value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} USDC`;
  }
  // USD default.
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * EPIC-029 STORY-1716 Part 5 — dual-display formatter for the
 * USDC-as-truth + local-fiat-projection rule (founder locked
 * vision 2026-05-02 PM).
 *
 *   "Saldo: $X USDC (≈ R$Y,YY hoje)" / "Saldo: $X USDC (≈ $Y CAD hoje)"
 *
 * Returns `{ primary, secondary }` so the caller controls layout
 * (large+small text on a card, side-by-side on a pill, primary-only
 * on a tight mobile slot, etc). Pure function; SSR-safe.
 *
 * Behavior:
 *   - When the wallet is already in the user's display currency,
 *     `secondary` is `null` (no projection needed — they ARE
 *     looking at their currency natively).
 *   - When the wallet is USDC and display is a fiat (BRL/CAD/MXN/
 *     INR), primary = USDC, secondary = "≈ R$Y,YY hoje" (or local
 *     equivalent).
 *   - When the wallet is fiat and display is USDC, primary = USDC
 *     (the canonical unit per the locked vision), secondary = the
 *     fiat value.
 *   - When the rate is unavailable, secondary is null — the caller
 *     still has the primary to render.
 *
 * The "hoje" suffix is rendered by the caller when locale=pt; this
 * helper returns just the conversion. Caller composes the full
 * "(≈ R$X hoje)" string via the user's locale.
 */
export interface DualMoneyDisplay {
  /** Always the USDC-denominated (or wallet-canonical) value. */
  primary: string;
  /** Local-fiat projection. `null` when no conversion is needed
   *  (same currency) or the rate is unavailable. */
  secondary: string | null;
}

/**
 * 2026-05-04 — region-aware balance render. Wraps formatDualMoney
 * with the locked-vision rule that BRL projection (`(≈ R$Y)`) is
 * reserved for region=brazil only. Non-BR viewers must NEVER see
 * R$ anywhere — not as primary, not as secondary.
 *
 * Founder reported 2026-05-04 with screenshot: a US-flag (region=
 * global, displayCurrency=USD) viewer with a BRL-denominated wallet
 * was seeing "R$399,94 (≈ $79.99)" on the /home cash row + the
 * header pill. Both surfaces now route through this helper.
 *
 * Behavior:
 *   - region=brazil → existing dual rendering
 *     (USDC primary + BRL secondary, or BRL primary + USDC secondary,
 *     depending on wallet vs display)
 *   - region !== brazil AND displayCurrency !== BRL →
 *     convert wallet → displayCurrency, return single primary in
 *     displayCurrency, NO secondary, NO R$ anywhere. When the FX
 *     rate is loading and the wallet is BRL, returns the
 *     displayCurrency-formatted "0" placeholder rather than
 *     leaking R$ as a fallback.
 *   - region !== brazil AND displayCurrency === BRL → identity
 *     (caller explicitly chose BRL display somehow; honour it).
 *
 * Always uses full-precision formatMoney — never rounds cents away.
 * Compact (k/M) only kicks in at >= 1k via formatMoneyCompact's own
 * threshold.
 */
/**
 * 2026-05-04 follow-up — single helper for surfaces that already have
 * BRL-denominated cents and need to render them in the viewer's
 * displayCurrency. Replaces the per-file `fmtFx` patterns that
 * silently fell back to R$ when `usdcPerBrl` hadn't loaded yet (which
 * leaked R$ to US-flag viewers — see formatBalanceForRegion's
 * docstring for the canonical incident).
 *
 *   - displayCurrency === BRL → always render R$. (BR users.)
 *   - displayCurrency !== BRL + rate unavailable → "—" placeholder
 *     (NOT R$). The component re-renders once the rate loads.
 *   - displayCurrency !== BRL + rate loaded → convert + render in
 *     displayCurrency.
 *
 * `compact` flag picks formatMoneyCompact (e.g. R$1.2k) for badges.
 *
 * @deprecated 2026-05-09 — for cents that are TRULY in BRL only
 *   (legacy pre-STORY-1700-part-3 rows). New surfaces should call
 *   `formatUsdcCentsForDisplay` since the matcher now writes USDC
 *   cents. Treating USDC cents as BRL produces a 5× routing error
 *   (founder report 2026-05-09 "every dollar bet takes 5 dollars
 *   from balance"). Audit in flight; remove after the data layer
 *   is fully USDC-backfilled.
 */
export function formatBrlCentsForDisplay(
  cents: number,
  displayCurrency: MoneyCurrency,
  usdcPerBrl: number | null,
  options: { compact?: boolean } = {},
): string {
  if (!Number.isFinite(cents)) return "—";
  const fmt = options.compact ? formatMoneyCompact : formatMoney;
  if (displayCurrency === "BRL") return fmt(cents, "BRL");
  if (usdcPerBrl === null) return "—";
  const converted = convertCents(cents, "BRL", displayCurrency, usdcPerBrl);
  if (converted === null) return "—";
  return fmt(converted, displayCurrency);
}

/**
 * 2026-05-09 — USDC-as-truth twin of formatBrlCentsForDisplay. Use
 * this on surfaces where `cents` is already in USDC (matcher unit
 * post STORY-1700-part-3): wallet balances, position values, fee
 * legs, notification payouts, fresh trade rows.
 *
 *   - displayCurrency === USD or USDC → render directly (1:1).
 *   - displayCurrency === BRL + rate available → multiply by rate
 *     and render R$. The user-facing R$ number is what they actually
 *     pay/receive (BR-primary display flip 2026-05-08).
 *   - displayCurrency === BRL + rate missing → fall back to USDC
 *     formatting so the user sees a real number, not "—".
 *
 * Mirrors the inline `fmtFx` pattern PR #1613 introduced in
 * TradePanel and #1615 propagated to the close + multi-outcome
 * surfaces; this helper centralizes it so additional callers
 * don't re-implement (and re-bug) the routing.
 */
export function formatUsdcCentsForDisplay(
  usdcCents: number,
  displayCurrency: MoneyCurrency,
  usdcPerBrl: number | null,
  options: { compact?: boolean } = {},
): string {
  if (!Number.isFinite(usdcCents)) return "—";
  const fmt = options.compact ? formatMoneyCompact : formatMoney;
  if (displayCurrency === "USD" || displayCurrency === "USDC") {
    return fmt(usdcCents, displayCurrency);
  }
  if (usdcPerBrl === null || usdcPerBrl <= 0) {
    return fmt(usdcCents, "USDC");
  }
  const brlCents = Math.round(usdcCents * usdcPerBrl);
  return fmt(brlCents, "BRL");
}

/**
 * Region → local-fiat map for the BR-primary display flip pattern
 * (2026-05-08, extended 2026-05-21 to MX/CA/IN). Each region maps to
 * the single fiat its locale renders as primary; viewers in those
 * regions never see USDC as the lead on chrome / balance surfaces.
 */
const REGION_PRIMARY_FIAT: Record<string, MoneyCurrency | undefined> = {
  brazil: "BRL",
  mexico: "MXN",
  canada: "CAD",
  india: "INR",
};

export function formatBalanceForRegion(
  cents: number,
  walletCurrency: MoneyCurrency | null,
  displayCurrency: MoneyCurrency,
  usdcPerBrl: number | null,
  region: string,
  options: { compact?: boolean } = {},
): DualMoneyDisplay {
  const fmt = options.compact ? formatMoneyCompact : formatMoney;
  const source = walletCurrency ?? "USDC";

  // Region-primary display flip (2026-05-08 for BR, extended
  // 2026-05-21 to MX/CA/IN): when the viewer is IN a Phase-1 fiat
  // region AND their displayCurrency matches that region's local
  // fiat, render local-only. USDC moves behind the tooltip/advanced
  // view; the aunt-from-Brazil persona test applies to MX/CA/IN
  // viewers the same way it does to BR viewers.
  const localFiat = REGION_PRIMARY_FIAT[region];
  if (localFiat !== undefined && displayCurrency === localFiat) {
    if (source === localFiat) {
      return { primary: fmt(cents, localFiat), secondary: null };
    }
    const converted = convertCents(cents, source, localFiat, usdcPerBrl);
    if (converted !== null) {
      return { primary: fmt(converted, localFiat), secondary: null };
    }
    // Rate not loaded yet — render local-fiat zero placeholder rather
    // than leaking USDC to a region-flag viewer mid-mount.
    return { primary: fmt(0, localFiat), secondary: null };
  }

  // Non-region viewer who explicitly chose a local fiat (BRL/MXN/CAD/
  // INR) display: keep the existing dual rendering. Treat as power-
  // user surface; both legs are useful.
  if (REGION_PRIMARY_FIAT[region] === undefined && displayCurrency in {
    BRL: 1, MXN: 1, CAD: 1, INR: 1,
  }) {
    return formatDualMoney(
      cents,
      walletCurrency,
      displayCurrency,
      usdcPerBrl,
      options,
    );
  }

  // Default: render only displayCurrency primary.
  if (source === displayCurrency) {
    return { primary: fmt(cents, displayCurrency), secondary: null };
  }
  const converted = convertCents(cents, source, displayCurrency, usdcPerBrl);
  if (converted !== null) {
    return { primary: fmt(converted, displayCurrency), secondary: null };
  }
  // Rate unavailable. If wallet is a local fiat, do NOT fall back to
  // it — that re-introduces the cross-region leak. Render a
  // displayCurrency-formatted "0" placeholder; the surface re-renders
  // once the rate loads.
  if (source === "BRL" || source === "MXN" || source === "CAD" || source === "INR") {
    return { primary: fmt(0, displayCurrency), secondary: null };
  }
  return { primary: fmt(cents, source), secondary: null };
}

/**
 * EPIC-029 BR-primary display flip Story 2 (2026-05-08) — render a
 * USDC-denominated value with the user's `displayCurrencyPrimary`
 * preference deciding which side leads.
 *
 * Inputs are always USDC cents (the ledger truth). The helper
 * applies a one-leg conversion to BRL via the platform fx rate
 * (Story 2 takes the same rate the rest of the app uses; the
 * platform spread is folded in by the caller's fxRate argument
 * — Story 3 wires this through with the locked deposit/withdrawal
 * spread depending on context).
 *
 * Returns `{ primary, secondary }` so the caller controls layout.
 *
 * Behavior matrix (`displayCurrencyPrimary` × locale):
 *
 *   'BRL' primary + rate available
 *     → primary = "R$Y,YY", secondary = "$X USDC"
 *   'BRL' primary + rate missing
 *     → primary = "$X USDC", secondary = null  (graceful fallback)
 *   'USDC' primary + rate available
 *     → primary = "$X USDC", secondary = "R$Y,YY hoje"
 *   'USDC' primary + rate missing
 *     → primary = "$X USDC", secondary = null
 *
 * When `displayCurrencyPrimary='BRL'`, the BRL number INCLUDES the
 * platform fx spread by virtue of `usdcPerBrlWithSpread` being the
 * spread-adjusted rate the caller passed in. That's the load-bearing
 * difference vs the older formatDualMoney path: the user-facing BRL
 * number IS what they pay/receive, not a raw mid-market projection.
 *
 * Stories 3-4 thread this helper through TradePanel + receipts +
 * portfolio surfaces. Story 6's /settings/display toggle drives
 * `displayCurrencyPrimary`.
 */
export function formatMoneyPrimary(
  usdcCents: number | string,
  displayCurrencyPrimary: 'BRL' | 'USDC',
  usdcPerBrlWithSpread: number | null,
  options: { compact?: boolean } = {},
): DualMoneyDisplay {
  const fmt = options.compact ? formatMoneyCompact : formatMoney;
  const cents =
    typeof usdcCents === 'string' ? Number(usdcCents) : usdcCents;

  if (!Number.isFinite(cents)) {
    return { primary: '—', secondary: null };
  }

  // Default: USDC primary (preserves the pre-flip behavior for non-BR
  // users + crypto-native opt-outs).
  if (displayCurrencyPrimary === 'USDC') {
    if (usdcPerBrlWithSpread === null || usdcPerBrlWithSpread <= 0) {
      return { primary: fmt(cents, 'USDC'), secondary: null };
    }
    const brlCents = Math.round(cents * usdcPerBrlWithSpread);
    return {
      primary: fmt(cents, 'USDC'),
      secondary: fmt(brlCents, 'BRL'),
    };
  }

  // BR-primary: render BRL leading, USDC behind. When the rate is
  // missing fall back to USDC primary so the user still sees a real
  // number rather than a "—" or stale BRL projection.
  if (usdcPerBrlWithSpread === null || usdcPerBrlWithSpread <= 0) {
    return { primary: fmt(cents, 'USDC'), secondary: null };
  }
  const brlCents = Math.round(cents * usdcPerBrlWithSpread);
  return {
    primary: fmt(brlCents, 'BRL'),
    secondary: fmt(cents, 'USDC'),
  };
}

export function formatDualMoney(
  cents: number,
  walletCurrency: MoneyCurrency | null,
  displayCurrency: MoneyCurrency,
  usdcPerBrl: number | null,
  options: { compact?: boolean } = {},
): DualMoneyDisplay {
  const fmt = options.compact ? formatMoneyCompact : formatMoney;
  // Source = the wallet the cents are denominated in. Per the
  // locked vision the canonical unit is USDC; if the caller didn't
  // pass a wallet currency, assume USDC.
  const source = walletCurrency ?? "USDC";

  // Same currency: identity. No secondary needed — user is already
  // looking at the value in their target unit.
  if (source === displayCurrency) {
    return { primary: fmt(cents, source), secondary: null };
  }

  // Cross-currency: render the source as primary (USDC stays canon)
  // + render the converted value as secondary.
  const converted = convertCents(cents, source, displayCurrency, usdcPerBrl);
  if (converted === null) {
    // Rate unavailable — keep primary in source currency, no secondary.
    return { primary: fmt(cents, source), secondary: null };
  }
  return {
    primary: fmt(cents, source),
    secondary: fmt(converted, displayCurrency),
  };
}

/**
 * EPIC-028 STORY-1602 — compact money formatter for badges/cards.
 *
 *   formatMoneyCompact(12345, "USD")    → "$123"
 *   formatMoneyCompact(1234567, "BRL")  → "R$12k"
 *   formatMoneyCompact(123456789, "BRL")→ "R$1.2M"
 */
export function formatMoneyCompact(
  cents: string | number,
  currency: MoneyCurrency = "BRL",
): string {
  const n = typeof cents === "string" ? Number(cents) : cents;
  if (!Number.isFinite(n)) return "—";
  const value = Math.trunc(n) / 100;
  const fiat = FIAT_FORMAT_TABLE[currency];
  const prefix = fiat?.symbol ?? "$";
  const locale = fiat?.locale ?? "en-US";
  if (Math.abs(value) >= 1_000_000) {
    return `${prefix}${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${prefix}${(value / 1_000).toFixed(1)}k`;
  }
  // 2026-05-04 — preserve cents under 1k. Founder flagged "$80" pill
  // when the actual balance was $79.99 — Math.round() was rounding
  // the cents away. Sub-1k callers expect the same 2-decimal precision
  // formatMoney emits; the compact form only kicks in at >= 1k via
  // the k / M suffix.
  return `${prefix}${value.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * EPIC-028 STORY-1606 — locale-aware date + number formatters.
 *
 * Closed-beta scope: pt-BR + en-US.
 *
 *   formatDate("2026-04-29T00:00:00Z", "pt") → "29/04/2026"
 *   formatDate("2026-04-29T00:00:00Z", "en") → "4/29/2026"
 *   formatNumber(1234.56, "pt")              → "1.234,56"
 *   formatNumber(1234.56, "en")              → "1,234.56"
 *
 * Wide replacement pass across existing components is deferred
 * (touching 50+ render sites would create merge-conflict risk).
 * New code uses these helpers; existing code migrates incrementally
 * via PR-by-PR replacements as each component is touched.
 */
export type DisplayLocale = "pt" | "en";

export function formatDate(
  iso: string | Date,
  locale: DisplayLocale = "en",
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale === "pt" ? "pt-BR" : "en-US", options);
}

export function formatDateTime(
  iso: string | Date,
  locale: DisplayLocale = "en",
): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(locale === "pt" ? "pt-BR" : "en-US");
}

export function formatNumber(
  value: number,
  locale: DisplayLocale = "en",
  options?: Intl.NumberFormatOptions,
): string {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString(locale === "pt" ? "pt-BR" : "en-US", options);
}

/**
 * Locale-aware relative-date formatter.
 *
 * Pre-EPIC-053-STORY-4a (2026-05-08) this returned hardcoded English
 * ("in 2 months") regardless of viewer locale, which produced the
 * "Fecha in 2 months" English-leak bug on Brazilian-locale market
 * pages: the i18n template "Fecha {{rel}}" interpolated the English
 * `rel` argument and rendered half-Portuguese, half-English.
 *
 * Now uses `Intl.RelativeTimeFormat` with `numeric: 'auto'`, which
 * natively renders "today" / "tomorrow" / "yesterday" in the target
 * locale (or their per-locale equivalents: "hoje" / "amanhã" /
 * "ontem", "hoy" / "mañana" / "ayer", etc.). The unit-bucketing
 * logic stays the same (days < 7, weeks < 30 days, months < 365
 * days, years), only the rendering side localizes.
 *
 * Callers MUST pass the active locale from `useTranslation().i18n.language`
 * (or the equivalent in their context). Default `'en'` keeps anonymous
 * / pre-i18n callers correct.
 */
export function formatRelativeDate(iso: string, locale: string = 'en'): string {
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return '—';
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  const intlLocale = normalizeIntlLocale(locale);
  const rtf = new Intl.RelativeTimeFormat(intlLocale, { numeric: 'auto' });

  if (Math.abs(diffDays) < 7) {
    // Covers today / tomorrow / yesterday + ±2-6 days. numeric:'auto'
    // collapses 0/1/-1 to localized words; |diff|≥2 renders as e.g.
    // "in 3 days" / "em 3 dias" / "hace 3 días".
    return rtf.format(diffDays, 'day');
  }
  if (Math.abs(diffDays) < 30) {
    return rtf.format(Math.round(diffDays / 7), 'week');
  }
  if (Math.abs(diffDays) < 365) {
    return rtf.format(Math.round(diffDays / 30), 'month');
  }
  return rtf.format(Math.round(diffDays / 365), 'year');
}

/**
 * Map our short locale codes to Intl-recognized BCP-47 tags. Most are
 * passthrough; we expand bare 'pt'/'es'/'fr'/'hi' to the regional
 * variant so Intl picks up the locale's standard relative-time
 * vocabulary (e.g. 'pt' falls back to 'pt' which Intl handles, but
 * 'pt-BR' is more explicit + matches the rest of our locale story).
 */
function normalizeIntlLocale(locale: string): string {
  switch (locale) {
    case 'pt':
      return 'pt-BR';
    case 'es':
      return 'es-MX';
    case 'fr':
      return 'fr-CA';
    case 'hi':
      return 'hi-IN';
    case 'en':
      return 'en-US';
    default:
      return locale;
  }
}

/**
 * Lucide line-icon components per category. Replaced the legacy
 * `CATEGORY_ICONS` emoji map (removed 2026-04-30 after the lucide
 * sweep — every React render site now uses categoryIcon() below;
 * the og:image generator never imported it). Imported and used by
 * MarketCard, /markets/[slug] page header, MyOrdersAndPositions, etc.
 *
 * Callers receive a LucideIcon and render with their own size /
 * className (no hardcoded styling here so the same map drives the
 * 22px market-card variant and the 28px market-detail variant).
 */
import {
  Landmark,
  Trophy,
  Bitcoin,
  TrendingUp,
  Microscope,
  Film,
  Earth,
  BarChart3,
  type LucideIcon,
} from "lucide-react";

export const CATEGORY_ICON_COMPONENTS: Record<string, LucideIcon> = {
  politics: Landmark,
  sports: Trophy,
  crypto: Bitcoin,
  economics: TrendingUp,
  tech: Microscope,
  culture: Film,
  world: Earth,
};

/**
 * Lookup helper — returns the matching lucide icon or BarChart3 as
 * a generic fallback (matches the previous `?? "📊"` default behaviour
 * at every call site). Saves callers from having to import the
 * fallback themselves.
 */
export function categoryIcon(slug: string): LucideIcon {
  return CATEGORY_ICON_COMPONENTS[slug] ?? BarChart3;
}

export const CATEGORY_LABELS: Record<string, string> = {
  politics: "Politics",
  sports: "Sports",
  crypto: "Crypto",
  economics: "Economics",
  tech: "Tech",
  culture: "Culture",
  world: "World",
};
